import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { contentService } from '../../services/contentService';
import { suppliersService } from '../../services/suppliersService';
import { uploadService } from '../../services/uploadService';
import './WebsiteManagement.css';

const WebsiteManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('content');
  const [contentCards, setContentCards] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [agentSettings, setAgentSettings] = useState({
    selectedCategories: [],
    selectedSuppliers: [],
    selectedContent: []
  });

  const categories = [
    'Cruise', 'Theme Park', 'Hotel', 'Resort', 
    'All-Inclusive', 'Transportation', 'Flight', 'Tour/Activity'
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [contentResponse, blogResponse, suppliersResponse] = await Promise.all([
        contentService.cards.getAll(),
        contentService.blog.getAll({ agent_id: user.id }),
        suppliersService.getAll()
      ]);

      setContentCards(contentResponse.data || []);
      setBlogPosts(blogResponse.data || []);
      setSuppliers(suppliersResponse || []);

      // Load agent's website settings
      // TODO: Add endpoint to get agent website settings
      
    } catch (error) {
      console.error('Error loading website data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBlogSubmit = async (blogData) => {
    try {
      if (editingBlog) {
        await contentService.blog.update(editingBlog.id, blogData);
      } else {
        await contentService.blog.create({
          ...blogData,
          status: 'published'
        });
      }
      
      setShowBlogModal(false);
      setEditingBlog(null);
      loadData();
    } catch (error) {
      console.error('Error saving blog post:', error);
    }
  };

  const handleDeleteBlog = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await contentService.blog.delete(id);
        loadData();
      } catch (error) {
        console.error('Error deleting blog post:', error);
      }
    }
  };

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleSupplierToggle = (supplierId) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplierId) 
        ? prev.filter(s => s !== supplierId)
        : [...prev, supplierId]
    );
  };

  const handleSaveSettings = async () => {
    try {
      // TODO: Add endpoint to save agent website settings
      console.log('Saving settings:', {
        selectedCategories,
        selectedSuppliers,
        selectedContent: contentCards.filter(c => c.selected).map(c => c.id)
      });
      alert('Website settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  if (loading) {
    return (
      <div className="website-loading">
        <div className="loading-spinner"></div>
        <p>Loading website management...</p>
      </div>
    );
  }

  return (
    <div className="website-management">
      <div className="website-header">
        <h1>Website Management</h1>
        <p className="website-subtitle">Customize your agent website content and appearance</p>
      </div>

      {/* Navigation Tabs */}
      <div className="website-tabs">
        <button
          className={`website-tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          Content Selection
        </button>
        <button
          className={`website-tab ${activeTab === 'blog' ? 'active' : ''}`}
          onClick={() => setActiveTab('blog')}
        >
          Blog Posts
        </button>
        <button
          className={`website-tab ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => setActiveTab('preview')}
        >
          Preview Website
        </button>
      </div>

      {/* Content Selection Tab */}
      {activeTab === 'content' && (
        <div className="content-selection">
          <div className="content-section">
            <h2>Select Categories to Display</h2>
            <p className="section-description">Choose which travel categories to feature on your website</p>
            <div className="category-grid">
              {categories.map(category => (
                <label key={category} className="category-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category)}
                    onChange={() => handleCategoryToggle(category)}
                  />
                  <span className="category-label">{category}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="content-section">
            <h2>Select Suppliers to Feature</h2>
            <p className="section-description">Choose which suppliers to display on your website</p>
            <div className="suppliers-grid">
              {suppliers
                .filter(supplier => 
                  selectedCategories.length === 0 || 
                  selectedCategories.some(cat => supplier.categories?.includes(cat))
                )
                .map(supplier => (
                  <label key={supplier.id} className="supplier-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedSuppliers.includes(supplier.id)}
                      onChange={() => handleSupplierToggle(supplier.id)}
                    />
                    <div className="supplier-info">
                      <span className="supplier-name">{supplier.name}</span>
                      <span className="supplier-categories">
                        {supplier.categories?.join(', ')}
                      </span>
                    </div>
                  </label>
                ))}
            </div>
          </div>

          <div className="content-section">
            <h2>Available Content</h2>
            <p className="section-description">Select specific promotional content to display</p>
            <div className="content-cards-grid">
              {contentCards
                .filter(card => 
                  (selectedCategories.length === 0 || selectedCategories.includes(card.category)) &&
                  (selectedSuppliers.length === 0 || selectedSuppliers.includes(card.supplier_id))
                )
                .map(card => (
                  <div key={card.id} className="content-card-item">
                    <label className="content-checkbox">
                      <input
                        type="checkbox"
                        checked={card.selected || false}
                        onChange={(e) => {
                          const updated = contentCards.map(c => 
                            c.id === card.id ? { ...c, selected: e.target.checked } : c
                          );
                          setContentCards(updated);
                        }}
                      />
                      <div className="content-info">
                        <h3>{card.title}</h3>
                        <p>{card.description}</p>
                        <div className="content-meta">
                          <span className="content-supplier">{card.supplier_name}</span>
                          <span className="content-category">{card.category}</span>
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
            </div>
          </div>

          <div className="settings-actions">
            <button className="btn-save-settings" onClick={handleSaveSettings}>
              Save Website Settings
            </button>
          </div>
        </div>
      )}

      {/* Blog Posts Tab */}
      {activeTab === 'blog' && (
        <div className="blog-management">
          <div className="blog-header">
            <h2>Blog Posts</h2>
            <button 
              className="btn-new-blog"
              onClick={() => {
                setEditingBlog(null);
                setShowBlogModal(true);
              }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Blog Post
            </button>
          </div>

          <div className="blog-posts-grid">
            {blogPosts.length === 0 ? (
              <div className="empty-state">
                <svg className="empty-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3>No blog posts yet</h3>
                <p>Create your first blog post to engage with clients</p>
              </div>
            ) : (
              blogPosts.map(post => (
                <div key={post.id} className="blog-post-card">
                  <div className="blog-post-image">
                    {post.files && post.files.length > 0 ? (
                      <img src={`/api/uploads/${post.files[0].filename}`} alt={post.title} />
                    ) : (
                      <div className="blog-post-placeholder">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="blog-post-content">
                    <h3>{post.title}</h3>
                    <p className="blog-excerpt">{post.excerpt || post.content.substring(0, 150) + '...'}</p>
                    <div className="blog-meta">
                      <span className="blog-category">{post.category}</span>
                      <span className="blog-date">
                        {new Date(post.publish_date || post.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="blog-actions">
                      <button 
                        className="btn-edit"
                        onClick={() => {
                          setEditingBlog(post);
                          setShowBlogModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteBlog(post.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <div className="website-preview">
          <div className="preview-header">
            <h2>Website Preview</h2>
            <a 
              href={`https://tpcrm.io/agent/${user.id}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-view-live"
            >
              View Live Website
              <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
          
          <div className="preview-iframe-container">
            <iframe
              src={`https://tpcrm.io/agent/${user.id}/preview`}
              title="Website Preview"
              className="preview-iframe"
            />
          </div>
        </div>
      )}

      {/* Blog Modal */}
      {showBlogModal && (
        <BlogModal
          blog={editingBlog}
          onClose={() => {
            setShowBlogModal(false);
            setEditingBlog(null);
          }}
          onSave={handleBlogSubmit}
        />
      )}
    </div>
  );
};

// Blog Modal Component
const BlogModal = ({ blog, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: blog?.title || '',
    content: blog?.content || '',
    category: blog?.category || 'Travel Tips',
    excerpt: blog?.excerpt || '',
    status: blog?.status || 'published',
    file_ids: []
  });
  const [uploadedFiles, setUploadedFiles] = useState(blog?.files || []);
  const [uploading, setUploading] = useState(false);

  const blogCategories = [
    'Travel Tips', 'Destination Guides', 'Cruise News',
    'Theme Parks', 'Travel Deals', 'Client Stories',
    'Travel Insurance', 'Packing Tips', 'Travel Safety'
  ];

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(file => uploadService.upload(file));
      const results = await Promise.all(uploadPromises);
      
      const newFiles = results.map(result => result.file);
      setUploadedFiles([...uploadedFiles, ...newFiles]);
      setFormData({
        ...formData,
        file_ids: [...formData.file_ids, ...newFiles.map(f => f.id)]
      });
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content blog-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{blog ? 'Edit Blog Post' : 'New Blog Post'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="blog-form">
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="Enter blog post title"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {blogCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Excerpt (optional)</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows={2}
              placeholder="Brief description of the blog post"
            />
          </div>

          <div className="form-group">
            <label>Content *</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={10}
              required
              placeholder="Write your blog post content here..."
            />
          </div>

          <div className="form-group">
            <label>Images</label>
            <div className="file-upload-area">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                id="blog-images"
              />
              <label htmlFor="blog-images" className="file-upload-label">
                {uploading ? 'Uploading...' : 'Choose Images'}
              </label>
              
              {uploadedFiles.length > 0 && (
                <div className="uploaded-files">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="uploaded-file">
                      <img 
                        src={`/api/uploads/${file.filename}`} 
                        alt={file.original_name} 
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
                          setFormData({
                            ...formData,
                            file_ids: formData.file_ids.filter(id => id !== file.id)
                          });
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {blog ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WebsiteManagement;
